import { BiomassUnit, LayerTypeGroupId, LayerTypeId, LayerTypeSubgroupId } from "../../generated/enums"
import { param } from "../../generated/labels"
import { flatten, partition } from "../../utils/array"
import { IDataLayer, IDataLayerType } from "../types"
import { MgrLayer, MgrLayerType } from "./types"

const excludedTypes = new Set([
    LayerTypeId.NutrientRequirements,
    LayerTypeId.NutrientApplications
])

export function setupTypes(types: IDataLayerType[], holdingLayers: IDataLayer[] | "all") {

    let includeSatellite = true

    if (holdingLayers !== "all") {

        const [satellite, std] = partition(holdingLayers, l => l.Satellite)
        includeSatellite = Boolean(satellite.length)

        const holdingTypes = new Set(std.map(l => l.TypeId))

        types = types
            .filter(t => holdingTypes.has(t.TypeId))
            .filter(t => !excludedTypes.has(t.TypeId))

    }

    const groups = getGroups(types)

    if (includeSatellite) {
        groups.push(getSatelliteTypeGroup(types))
    }

    return groups

}

export function flattenTypes(types: MgrLayerType[]): MgrLayerType[] {
    return flatten(types, t => t.children)
}

export function filterLayersForType(layers: MgrLayer[], type: MgrLayerType) {

    const types = new Set(flattenTypes([type]).filter(t => t.type).map(t => t.type.TypeId))

    if (types.size == 0) {
        return layers
    }

    const result = layers.filter(l => types.has(l.typeId))

    return result

}

function getGroups(types: IDataLayerType[]) {

    let nodes = param.LayerTypeGroupId().map(tg => {

        const subset = types.filter(t => t.GroupId == tg.value)

        if (subset.length == 0) {
            return undefined
        }

        const key = getGroupKey(tg.value)

        const node: MgrLayerType = {

            key,
            name: tg.text,
            canSelect: true,
            level: 1,

            children: getSubgroups(tg.value, subset),

            group: tg.value,
            subgroup: undefined,

            type: subset.length == 1
                ? subset[0]
                : undefined,

            layerFilter: excludeSatelliteLayer

        }

        return node

    })

    nodes = nodes.filter(n => !!n)

    return nodes

}

function getSubgroups(group: LayerTypeGroupId, types: IDataLayerType[]) {

    let nodes = param.LayerTypeSubgroupId().map(sg => {

        const subset = types.filter(t => t.SubgroupId == sg.value)

        if (subset.length == 0) {
            return undefined
        }

        const key = getGroupKey(group, sg.value)

        const node: MgrLayerType = {

            key,
            name: sg.text,
            canSelect: true,
            level: 2,

            children: subset.map(t => getLayerType(t, 3)),

            group,
            subgroup: sg.value,

            type: subset.length == 1
                ? subset[0]
                : undefined,

            layerFilter: excludeSatelliteLayer

        }

        return node

    })

    nodes = nodes.concat(
        types.filter(t => !t.SubgroupId).map(t => getLayerType(t, 2))
    )

    nodes = nodes.filter(n => !!n)

    return nodes

}

function getLayerType(type: IDataLayerType, level: number): MgrLayerType {

    const key = getTypeKey(type.TypeId)

    return {
        key,
        name: type.Name,
        canSelect: true,
        level,
        children: [],
        group: type.GroupId,
        subgroup: type.SubgroupId,
        type,
        layerFilter: excludeSatelliteLayer
    }
}

function getTypeKey(type: LayerTypeId) {
    return "t:" + type
}

function getGroupKey(group: LayerTypeGroupId, subgroup?: LayerTypeSubgroupId) {

    const key = "g:" + String(group)

    return subgroup
        ? key + ":" + subgroup
        : key

}

function excludeSatelliteLayer(layer: MgrLayer) {
    return !layer.layer.Satellite
}

function getSatelliteTypeGroup(types: IDataLayerType[]): MgrLayerType {

    const biomass = types.find(t => t.TypeId === LayerTypeId.Biomass)
    const picture = types.find(t => t.TypeId === LayerTypeId.Picture)

    return {
        key: "g:sat",
        name: "Satellite",
        level: 1,
        layerFilter: l => !!l.layer.Satellite,
        group: undefined,
        subgroup: undefined,
        type: undefined,
        canSelect: true,
        children: [
            subType({
                key: "g:sat:ndvi",
                name: "NDVI",
                type: biomass,
                layerFilter: l => l.layer.Satellite && (l.layer.TypeId == LayerTypeId.Biomass) && (l.layer.Unit == BiomassUnit.NDVI)
            }),
            subType({
                key: "g:sat:chi",
                name: "CHI",
                type: biomass,
                layerFilter: l => l.layer.Satellite && (l.layer.TypeId == LayerTypeId.Biomass) && (l.layer.Unit == BiomassUnit.CHI)
            }),
            subType({
                key: "g:sat:rgb",
                name: "RGB",
                type: picture,
                layerFilter: l => l.layer.Satellite && (l.layer.TypeId == LayerTypeId.Picture)
            }),
        ],
    }

    function subType(type: Pick<MgrLayerType, "key" | "name" | "type" | "layerFilter">): MgrLayerType {
        return {
            ...type,
            level: 2,
            canSelect: true,
            group: undefined,
            subgroup: undefined,
            children: []
        }
    }

}
